import { createFileRoute, Link } from "@tanstack/react-router";
import { Breadcrumbs, buildBreadcrumbLd } from "@/components/site/Breadcrumbs";
import { socialImageMeta } from "@/lib/social";
import { makeSeoTitle } from "@/lib/seo-title";
import { makeSeoDescription } from "@/lib/seo-description";
import { ArticleDisclaimer } from "@/components/site/ArticleDisclaimer";
import { COUNTRY_EVENTS, TOTAL_EVENT_COUNT } from "@/data/country-events";
import { roundFlagUrl } from "@/data/countries";
import { CalendarDays, MapPin, Ticket, ArrowRight } from "lucide-react";

const CANONICAL = "https://lifewestway.com/events";
const TITLE = makeSeoTitle("Global Events Calendar 2026");
const DESCRIPTION = makeSeoDescription(
  "Country-by-country events calendar for expats: dates, times, venues, prices and official ticket or RSVP links for festivals, sport, tech and culture."
);

const CRUMBS = [{ label: "Home", href: "/" }, { label: "Events" }];

export const Route = createFileRoute("/events/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:site_name", content: "LifeWestWay" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      ...socialImageMeta({ alt: "LifeWestWay global events calendar" }),
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(buildBreadcrumbLd(CRUMBS)),
      },
    ],
  }),
  component: EventsIndex,
});

function EventsIndex() {
  return (
    <main id="main-content" className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <Breadcrumbs items={CRUMBS} />

      <section className="relative overflow-hidden rounded-3xl border border-border/60 bg-gradient-to-br from-card/80 via-card/40 to-background/60 p-8 sm:p-12">
        <span aria-hidden className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-accent/20 blur-3xl" />
        <div className="relative">
          <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">
            <CalendarDays className="h-3.5 w-3.5" />
            {COUNTRY_EVENTS.length} countries · {TOTAL_EVENT_COUNT} events
          </span>
          <h1 className="mt-4 font-display text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Global <span className="text-accent">Events Calendar</span> 2026–2027
          </h1>
          <p className="mt-4 max-w-3xl text-base text-foreground/75 sm:text-lg">
            Every country page below lists the events worth planning your year around, with the
            exact dates, start times, venue addresses, realistic price ranges and a direct link to
            the official ticket, registration or RSVP page. We deliberately avoid resale sites:
            each link goes to the organiser, the venue or the relevant government tourism body.
          </p>
          <p className="mt-3 max-w-3xl text-sm text-foreground/70">
            If you have just relocated, these dates matter more than they look. Conference weeks
            are the densest recruiter contact you will get, national days tell you when offices
            and banks close, and neighbourhood festivals are usually the fastest way to meet
            people who are not other newcomers. Check every date on the official page before
            booking travel — organisers move dates and on-sale windows each season.
          </p>
        </div>
      </section>

      <h2 className="mt-12 font-display text-2xl font-bold text-accent">Choose a country</h2>
      <p className="mt-2 max-w-3xl text-sm text-foreground/70">
        Six full guide countries plus fourteen additional destinations from our interactive world
        map. Each page opens with a local planning brief covering transport, booking windows and
        seasonal pricing.
      </p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {COUNTRY_EVENTS.map((c) => {
          const next = c.events[0];
          return (
            <Link
              key={c.slug}
              to="/events/$country"
              params={{ country: c.slug }}
              title={`See events in ${c.name}`}
              className="group rounded-2xl border border-border bg-card p-5 transition-all hover:-translate-y-0.5 hover:border-accent/60 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <div className="flex items-center gap-3">
                <img src={roundFlagUrl(c.code)} alt={`${c.name} flag`} width={36} height={36} loading="lazy" className="h-9 w-9 rounded-full ring-1 ring-border" />
                <h3 className="font-display text-lg font-bold text-foreground">{c.name}</h3>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-foreground/70">
                {c.events.length} listed events · next up {next.name}, {next.dateLabel}.
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-accent">
                View {c.name} events <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </span>
            </Link>
          );
        })}
      </div>

      <h2 className="mt-14 font-display text-2xl font-bold text-accent">How to use this calendar</h2>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-5">
          <h3 className="flex items-center gap-2 font-display text-base font-bold text-foreground">
            <CalendarDays className="h-4 w-4 text-accent" /> Dates and times
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-foreground/70">
            Every listing shows the full date range plus daily running hours, because gate times
            and performance times are rarely the same thing. Where an event moves with a lunar or
            religious calendar we say so rather than pretending the date is fixed.
          </p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <h3 className="flex items-center gap-2 font-display text-base font-bold text-foreground">
            <MapPin className="h-4 w-4 text-accent" /> Location detail
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-foreground/70">
            Each event carries the venue name and a street address you can paste straight into a
            maps app, which matters most for street festivals where the &quot;venue&quot; is a
            route or a district rather than a building.
          </p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <h3 className="flex items-center gap-2 font-display text-base font-bold text-foreground">
            <Ticket className="h-4 w-4 text-accent" /> Tickets and RSVP
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-foreground/70">
            Paid events link to the official box office; free events link to the organiser&apos;s
            programme or registration page and are labelled Free / RSVP so you know before you
            click whether a payment is involved.
          </p>
        </div>
      </div>

      <ArticleDisclaimer />
    </main>
  );
}
